import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { Typography, TableList, FullRow } from 'seagull';
import { ServicePriceEditor } from '~/components/ServicePriceEditor/ServicePriceEditor';
import ServicePriceEditorPopover from '~/components/ServicePriceEditorPopover/ServicePriceEditorPopover';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { serviceTemplateModel, serviceCostModel, serviceModel } from '~/models';
import { ServiceSource } from '~/models/service/model';
import { isOneTimeInStages } from '~/models/service-template';
import { renderDecimalDiscountTitle, renderRangeDiscountTitle } from '~/pages/DraftsDashboard/EditDraftsTable/serviceCells/serviceDiscountTitles';
import { utils } from '~/utils';
import styles from './ServiceRowPrice.module.scss';
function getPriceData(cost) {
    return {
        price: serviceCostModel.isRangePrice(cost) ? {
            minPrice: serviceModel.minPrice({
                cost
            }),
            maxPrice: serviceModel.price({
                cost
            })
        } : serviceModel.price({
            cost
        }),
        hasDiscount: serviceModel.discount({
            cost
        }) != null,
        discountedPrice: serviceCostModel.isRangePrice(cost) ? {
            minPrice: serviceModel.minPriceFinal({
                cost
            }),
            maxPrice: serviceModel.applyDiscount({
                cost
            })
        } : serviceModel.finalPrice({
            cost
        }, 1)
    };
}
export function ServiceRowPrice(param) {
    let { service, onInlineEdit, onInputFocus, onInputBlur } = param;
    var _localService_cost_discount;
    const { INLINE_EDIT_SERVICE_PRICING } = useFeatureFlags();
    const [localService, setLocalService] = useState(service);
    const [{ price, hasDiscount, discountedPrice }, setPriceData] = useState(()=>getPriceData(service.cost));
    const [anchorEl, setAnchorEl] = useState(null);
    const [priceEditorOpen, setPriceEditorOpen] = useState(false);
    useEffect(()=>{
        setLocalService(service);
    }, [
        service
    ]);
    useEffect(()=>{
        setPriceData(getPriceData(localService.cost));
    }, [
        localService
    ]);
    const serviceAsService = useMemo(()=>{
        const baseService = serviceModel.fromServiceTemplate(service, ServiceSource.Draft);
        return _object_spread_props(_object_spread({}, baseService), {
            cost: localService.cost
        });
    }, [
        service,
        localService
    ]);
    const isVariable = serviceTemplateModel.isVariablePrice(localService.cost);
    const unitName = serviceCostModel.formattedUnitNameShort(localService.cost);
    const isInvalidDiscount = (isVariable || serviceTemplateModel.isRangePrice(localService.cost)) && !!((_localService_cost_discount = localService.cost.discount) === null || _localService_cost_discount === void 0 ? void 0 : _localService_cost_discount.isValue());
    const renderDecimalDiscountedPrice = (discountedPrice)=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    as: "span",
                    testId: "service-library-row-discounted-price",
                    children: utils.formatDollarAmount(discountedPrice)
                }),
                !isInvalidDiscount && price instanceof Decimal && hasDiscount && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        ' ',
                        /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            skin: "disabled",
                            textDecoration: "line-through",
                            testId: "service-library-row-original-price",
                            children: [
                                "$",
                                utils.formatNumberWithCommas(price)
                            ]
                        })
                    ]
                })
            ]
        });
    const renderNonDecimalDiscountedPrice = (discountedPrice)=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    as: "span",
                    testId: "service-library-row-discounted-price",
                    children: [
                        !!discountedPrice.minPrice && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                utils.formatDollarAmount(discountedPrice.minPrice),
                                ' - '
                            ]
                        }),
                        discountedPrice.minPrice == null && 'Up to ',
                        discountedPrice.maxPrice instanceof Decimal ? utils.formatDollarAmount(discountedPrice.maxPrice) : ''
                    ]
                }),
                price != null && !isInvalidDiscount && !(price instanceof Decimal) && hasDiscount && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        ' ',
                        /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            skin: "disabled",
                            textDecoration: "line-through",
                            testId: "service-library-row-original-price",
                            children: [
                                price.minPrice instanceof Decimal && /*#__PURE__*/ _jsx(_Fragment, {
                                    children: /*#__PURE__*/ _jsxs(Typography, {
                                        as: "span",
                                        skin: "disabled",
                                        textDecoration: "line-through",
                                        testId: "service-library-row-original-price",
                                        children: [
                                            utils.formatDollarAmount(price.minPrice),
                                            ' - '
                                        ]
                                    })
                                }),
                                "".concat(price.maxPrice instanceof Decimal ? utils.formatDollarAmount(price.maxPrice) : '')
                            ]
                        })
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(FullRow, {
        className: styles.root,
        testId: "service-library-row-price",
        children: [
            /*#__PURE__*/ _jsx(TableList.InlineControl.Button, {
                testId: "service-library-row-price-btn",
                error: false,
                tooltipProps: {
                    title: discountedPrice == null ? '' : discountedPrice instanceof Decimal ? renderDecimalDiscountTitle(discountedPrice, price, hasDiscount, isVariable, unitName) : renderRangeDiscountTitle(discountedPrice, price, hasDiscount)
                },
                onClick: (e)=>{
                    setAnchorEl(e.currentTarget);
                    setPriceEditorOpen(true);
                    onInputFocus === null || onInputFocus === void 0 ? void 0 : onInputFocus();
                },
                children: discountedPrice == null ? /*#__PURE__*/ _jsx(Typography, {
                    testId: "service-library-row-discounted-price",
                    children: "________"
                }) : discountedPrice instanceof Decimal ? renderDecimalDiscountedPrice(discountedPrice) : renderNonDecimalDiscountedPrice(discountedPrice)
            }),
            isVariable && /*#__PURE__*/ _jsxs(Typography, {
                className: styles.unitName,
                skin: "secondary",
                ellipsis: true,
                tooltipProps: {
                    title: unitName
                },
                children: [
                    "/ ",
                    unitName
                ]
            }),
            INLINE_EDIT_SERVICE_PRICING ? /*#__PURE__*/ _jsx(ServicePriceEditorPopover, {
                serviceOrigin: "template",
                onServiceChange: (service)=>{
                    const serviceToServiceTemplate = serviceTemplateModel.fromService(service, localService);
                    setLocalService(serviceToServiceTemplate);
                    onInlineEdit(serviceToServiceTemplate);
                },
                serviceWithChanges: serviceAsService,
                service: serviceAsService,
                editorAnchor: anchorEl,
                isOpen: priceEditorOpen,
                setIsOpen: setPriceEditorOpen
            }) : /*#__PURE__*/ _jsx(ServicePriceEditor, {
                cost: localService.cost,
                editorAnchor: anchorEl,
                isOpen: priceEditorOpen,
                isBillInStages: isOneTimeInStages(service.billing),
                setIsOpen: (isOpen)=>{
                    if (!isOpen) onInputBlur === null || onInputBlur === void 0 ? void 0 : onInputBlur();
                    setPriceEditorOpen(isOpen);
                },
                onCostChange: (cost)=>{
                    setLocalService(_object_spread_props(_object_spread({}, localService), {
                        cost
                    }));
                    onInlineEdit({
                        cost
                    });
                }
            })
        ]
    });
}
